import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * THE SCREEN KEEPS UP WITH THE CLOCK, AND WITH THE RECORD (20 Oct 2026).
 *
 * Nick: "a lot of times i go back and it still says morning but it no longer
 * is morning or it shows me my old check in score". An installed app is
 * RESUMED far more often than it is opened — iOS keeps the page alive in the
 * background and hands it back untouched — and Today read the clock once per
 * render and fetched its data once per mount. Nothing re-rendered it and
 * nothing remounted it, so the morning face, the date line's "morning" and the
 * check-in word all stayed wherever they were when he last looked.
 *
 * Two things, then, and they are different:
 *
 *   - THE CLOCK. `useNow()` is a Date that moves on the minute while the app is
 *     in front, and at once when it comes back. Anything a screen derives from
 *     the time — the face, "morning", which day before 4am is — reads it from
 *     here instead of calling `new Date()` during a render nobody asked for.
 *
 *   - THE RECORD. `useOnResume(fn)` runs `fn` when the app comes back after a
 *     minute or more away, so a check-in made on another device, a sleep the
 *     Shortcut sent overnight, or a dose that came due is fetched again. It
 *     refetches in place: nothing is remounted, so a half-written entry in the
 *     door or a note in the check-in form is never thrown away by a refresh.
 *
 * "Back" is any of: the page becoming visible, the window regaining focus, a
 * page restored from the back-forward cache, or a minute tick that arrives
 * minutes late (a laptop that slept with the app open fires no visibility
 * event). At most once a minute, so flicking between apps costs nothing.
 *
 * Owner screens only. A recipient's page never subscribes: every fetch of a
 * shared page is an opening in the access log, and the page does not change
 * under a recipient's eyes anyway.
 */

/** Away at least this long before the record is fetched again. */
export const RESUME_AFTER_MS = 60_000;
/** A minute tick this late means the device slept with the app in front. */
const LATE_TICK_MS = 150_000;
const RESUME_EVENT = 'adhd-me:resume';

const NowContext = createContext<Date | null>(null);

export function FreshProvider({ children }: { children: ReactNode }): JSX.Element {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let lastFresh = Date.now();
    let lastTick = Date.now();
    let timer: number | undefined;

    const resume = (): void => {
      lastFresh = Date.now();
      window.dispatchEvent(new Event(RESUME_EVENT));
    };
    const schedule = (): void => {
      window.clearTimeout(timer);
      // On the minute, plus a beat, so "11:00" is read after it is 11:00.
      timer = window.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 250);
    };
    function tick(): void {
      const t = Date.now();
      const late = t - lastTick > LATE_TICK_MS;
      lastTick = t;
      setNow(new Date(t));
      schedule();
      if (late && document.visibilityState === 'visible' && t - lastFresh >= RESUME_AFTER_MS) resume();
    }
    const back = (): void => {
      if (document.visibilityState !== 'visible') return;
      const t = Date.now();
      lastTick = t;
      setNow(new Date(t));
      schedule();
      if (t - lastFresh >= RESUME_AFTER_MS) resume();
    };
    const onShow = (e: PageTransitionEvent): void => {
      if (e.persisted) back();
    };

    schedule();
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
      window.removeEventListener('pageshow', onShow);
    };
  }, []);

  return <NowContext.Provider value={now}>{children}</NowContext.Provider>;
}

/** The time, moving: on the minute while in front, and at once on coming back. */
export function useNow(): Date {
  return useContext(NowContext) ?? new Date();
}

/**
 * Runs `fn` when the app comes back after a minute or more away.
 *
 * Not on mount — the screen's own effect already fetched. `fn` is read through
 * a ref, so it always sees the render's current day and never needs to be in
 * anybody's dependency list.
 */
export function useOnResume(fn: () => void): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const run = (): void => ref.current();
    window.addEventListener(RESUME_EVENT, run);
    return () => window.removeEventListener(RESUME_EVENT, run);
  }, []);
}
